/**
 * Consolidated Brand Context Provider
 * Manages brand configuration and theming across all Equilateral UI applications
 *
 * Features:
 * - Domain-based brand detection
 * - Brand override via localStorage for testing
 * - Fallback colors for offline/error scenarios
 * - Dynamic CSS variable injection for theming
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

/**
 * Brand Configuration Interface
 * Matches backend database schema for brands table
 */
export interface BrandConfig {
  Brand_Code: string;
  Brand_Name: string;
  Domain: string;
  Primary_Color: string;
  Secondary_Color: string;
  Logo_URL: string | null;
  Favicon_URL: string | null;
  Enabled_Features: string[];
  Enabled_Agents: string[];
  Pricing_Tier: 'free' | 'starter' | 'professional' | 'enterprise';
  Monthly_Price: number;
  Annual_Price: number;
  Is_Active: boolean;
}

/**
 * Brand Color Themes - Hardcoded defaults for offline/fallback
 */
export const BRAND_COLORS: Record<string, { primary: string; name: string }> = {
  modelmind: { primary: '#10B981', name: 'ModelMind' },
  patternforge: { primary: '#8B5CF6', name: 'PatternForge' },
  specmind: { primary: '#3B82F6', name: 'SpecMind' },
  powerspec: { primary: '#7C3AED', name: 'PowerSpec' },
  velocitymind: { primary: '#EF4444', name: 'VelocityMind' },
  complianceforge: { primary: '#059669', name: 'ComplianceForge' },
  agencyflow: { primary: '#F59E0B', name: 'AgencyFlow' },
  equilateral: { primary: '#6366F1', name: 'Equilateral' },
};

/**
 * Domain to Brand Code Mapping
 * Maps root domains to their brand codes
 */
const DOMAIN_TO_BRAND: Record<string, string> = {
  'modelmind.dev': 'modelmind',
  'patternforge.dev': 'patternforge',
  'specmind.io': 'specmind',
  'powerspec.ai': 'powerspec',
  'productspec.ai': 'powerspec',
  'velocitymind.io': 'velocitymind',
  'complianceforge.io': 'complianceforge',
  'agencyflow.dev': 'agencyflow',
  'equilateral.ai': 'equilateral',
};

interface BrandContextType {
  brand: BrandConfig | null;
  brandCode: string;
  isLoading: boolean;
  error: string | null;
  isFeatureEnabled: (featureName: string) => boolean;
  isAgentEnabled: (agentName: string) => boolean;
  primaryColor: string;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

/**
 * Extract brand code from domain
 * Examples:
 *   modelmind.dev -> modelmind
 *   app.modelmind.dev -> modelmind
 *   app.powerspec.ai -> powerspec
 *   localhost:3000 -> equilateral (default)
 *
 * Supports:
 * - Root domains (modelmind.dev)
 * - Subdomains (app.modelmind.dev)
 * - Brand override via localStorage for testing
 */
function extractBrandFromDomain(): string {
  // Check for brand override in localStorage for testing
  const override = localStorage.getItem('brand_override');
  if (override) {
    return override;
  }

  const hostname = window.location.hostname.toLowerCase();

  // Development/localhost defaults to equilateral
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'equilateral';
  }

  // Extract root domain (handle subdomains)
  // app.modelmind.dev -> modelmind.dev
  // api.powerspec.ai -> powerspec.ai
  const parts = hostname.split('.');

  // Try to match full hostname first (for exact matches)
  if (DOMAIN_TO_BRAND[hostname]) {
    return DOMAIN_TO_BRAND[hostname];
  }

  // Try to match root domain (last 2 parts)
  // app.modelmind.dev -> modelmind.dev
  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join('.');
    if (DOMAIN_TO_BRAND[rootDomain]) {
      return DOMAIN_TO_BRAND[rootDomain];
    }
  }

  // Fallback: check if any domain part matches a known brand
  for (const part of parts) {
    const brandKey = part.toLowerCase();
    if (BRAND_COLORS[brandKey]) {
      return brandKey;
    }
  }

  // Default to equilateral
  return 'equilateral';
}

export interface BrandProviderProps {
  children: ReactNode;
  apiBaseUrl?: string;
}

export function BrandProvider({ children, apiBaseUrl }: BrandProviderProps) {
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [brandCode] = useState<string>(extractBrandFromDomain);

  // Determine API base URL (allow override via prop, otherwise use environment)
  const effectiveApiBaseUrl = apiBaseUrl || process.env.REACT_APP_API_URL || '';

  /**
   * Adjust color brightness
   * @param color - Hex color
   * @param percent - Positive = lighter, Negative = darker
   */
  const adjustColorBrightness = useCallback((color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + Math.round(2.55 * percent)));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + Math.round(2.55 * percent)));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + Math.round(2.55 * percent)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }, []);

  /**
   * Apply brand theme colors to CSS variables
   */
  const applyBrandTheme = useCallback((primaryColor: string) => {
    const root = document.documentElement;

    // Set primary color and variants
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-primary-hover', adjustColorBrightness(primaryColor, -10));
    root.style.setProperty('--brand-primary-light', adjustColorBrightness(primaryColor, 40));
    root.style.setProperty('--brand-primary-dark', adjustColorBrightness(primaryColor, -20));

    // Update meta theme color for mobile browsers
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', primaryColor);
    }
  }, [adjustColorBrightness]);

  useEffect(() => {
    async function fetchBrandConfig() {
      setIsLoading(true);
      setError(null);

      try {
        // Use the correct SAM template path: /api/brands/brandConfigGet
        const response = await fetch(`${effectiveApiBaseUrl}/api/brands/brandConfigGet?brand_code=${brandCode}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch brand config: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Failed to load brand configuration');
        }

        // Public endpoint returns brand directly in data (not wrapped in Records)
        const brandData = result.data;

        if (!brandData) {
          throw new Error(`Brand '${brandCode}' not found`);
        }

        // Map snake_case from API to Pascal_Case for BrandConfig interface
        const mappedBrand: BrandConfig = {
          Brand_Code: brandData.brand_code,
          Brand_Name: brandData.brand_name,
          Domain: brandData.custom_domain || '',
          Primary_Color: brandData.primary_color || '#6366F1',
          Secondary_Color: brandData.secondary_color || '#4F46E5',
          Logo_URL: brandData.logo_url,
          Favicon_URL: brandData.favicon_url,
          Enabled_Features: brandData.enabled_features || [],
          Enabled_Agents: brandData.enabled_agents || [],
          Pricing_Tier: 'professional',
          Monthly_Price: 0,
          Annual_Price: 0,
          Is_Active: true,
        };

        setBrand(mappedBrand);

        // Apply theme to document
        applyBrandTheme(mappedBrand.Primary_Color);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error loading brand';
        console.error('[BrandContext] Error fetching brand config:', errorMessage);
        setError(errorMessage);

        // Apply fallback theme based on brand code
        const fallbackColor = BRAND_COLORS[brandCode]?.primary || BRAND_COLORS.equilateral.primary;
        applyBrandTheme(fallbackColor);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrandConfig();
  }, [brandCode, effectiveApiBaseUrl, applyBrandTheme]);

  const isFeatureEnabled = (featureName: string): boolean => {
    if (!brand) return false;
    return brand.Enabled_Features.includes(featureName);
  };

  const isAgentEnabled = (agentName: string): boolean => {
    if (!brand) return false;
    return brand.Enabled_Agents.includes(agentName);
  };

  const primaryColor = brand?.Primary_Color || BRAND_COLORS[brandCode]?.primary || BRAND_COLORS.equilateral.primary;

  const value: BrandContextType = {
    brand,
    brandCode,
    isLoading,
    error,
    isFeatureEnabled,
    isAgentEnabled,
    primaryColor,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

/**
 * Hook to check if current brand has a specific feature
 */
export function useFeatureEnabled(featureName: string): boolean {
  const { isFeatureEnabled } = useBrand();
  return isFeatureEnabled(featureName);
}

/**
 * Hook to check if current brand has access to a specific agent
 */
export function useAgentEnabled(agentName: string): boolean {
  const { isAgentEnabled } = useBrand();
  return isAgentEnabled(agentName);
}
